/**
 * Shared presigned-upload helpers.
 *
 * The presign → PUT → complete pattern is used by every large-file upload
 * (marketing, directory, reupload, cover, brief, newsletter). The browser
 * uploads the bytes straight to S3 via a presigned URL, so the file never
 * passes through the web server.
 */

/**
 * PUT a file/blob to a presigned S3 URL, reporting upload progress (0-100).
 */
export function putWithProgress(
  url: string,
  body: Blob,
  onProgress: (pct: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error("HTTP " + xhr.status));
    };
    xhr.onerror = () => reject(new Error("network"));
    xhr.send(body);
  });
}

/**
 * Downscale a JPEG image File to a maximum longest-side dimension, returning a
 * re-encoded JPEG Blob. Used for newsletter images (the legacy endpoint resized
 * server-side with GD). Returns the original file unchanged if it's already
 * within bounds or cannot be decoded.
 */
export async function downscaleJpeg(file: File, maxDim: number, quality = 0.9): Promise<Blob> {
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return file;

  const { width, height } = bitmap;
  if (width <= maxDim && height <= maxDim) {
    bitmap.close();
    return file;
  }

  let newW: number;
  let newH: number;
  if (width >= height) {
    newW = maxDim;
    newH = Math.round(height * (maxDim / width));
  } else {
    newH = maxDim;
    newW = Math.round(width * (maxDim / height));
  }

  const canvas = document.createElement("canvas");
  canvas.width = newW;
  canvas.height = newH;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close();
    return file;
  }
  ctx.drawImage(bitmap, 0, 0, newW, newH);
  bitmap.close();

  return await new Promise<Blob>((resolve) => {
    canvas.toBlob((blob) => resolve(blob ?? file), "image/jpeg", quality);
  });
}

/**
 * EXIF-orient, center-crop to a square and resize to `size`x`size`, returning a
 * re-encoded JPEG Blob. Mirrors the server-side GD processing in the legacy
 * profilepicupload.php. Falls back to the original file if it can't be decoded.
 */
export async function cropSquareJpeg(file: File, size: number, quality = 0.9): Promise<Blob> {
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" }).catch(
    () => null
  );
  if (!bitmap) return file;

  const { width, height } = bitmap;
  const square = Math.min(width, height);
  const sx = Math.floor((width - square) / 2);
  const sy = Math.floor((height - square) / 2);

  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close();
    return file;
  }
  ctx.drawImage(bitmap, sx, sy, square, square, 0, 0, size, size);
  bitmap.close();

  return await new Promise<Blob>((resolve) => {
    canvas.toBlob((blob) => resolve(blob ?? file), "image/jpeg", quality);
  });
}
